import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore';
import { db } from '../firebase';

const OwnerPaymentRecords = ({ ownerId }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, today, week, month
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    todayRevenue: 0,
    weekRevenue: 0,
    monthRevenue: 0,
    totalRevenue: 0
  });

  useEffect(() => {
    loadRecords();
  }, [filter, ownerId]);

  const loadRecords = async () => {
    if (!ownerId) return;
    
    setLoading(true);
    try {
      const recordsRef = collection(db, 'parkingRecords');
      let q;

      // Apply date filter
      const now = new Date();
      let startDate;

      switch (filter) {
        case 'today':
          startDate = new Date();
          startDate.setHours(0, 0, 0, 0);
          q = query(recordsRef, 
            where('ownerId', '==', ownerId),
            where('timestamp', '>=', startDate), 
            orderBy('timestamp', 'desc')
          );
          break;
        case 'week':
          startDate = new Date();
          startDate.setDate(now.getDate() - 7);
          q = query(recordsRef, 
            where('ownerId', '==', ownerId),
            where('timestamp', '>=', startDate), 
            orderBy('timestamp', 'desc')
          );
          break;
        case 'month':
          startDate = new Date();
          startDate.setMonth(now.getMonth() - 1);
          q = query(recordsRef, 
            where('ownerId', '==', ownerId),
            where('timestamp', '>=', startDate), 
            orderBy('timestamp', 'desc')
          );
          break;
        default:
          q = query(recordsRef, 
            where('ownerId', '==', ownerId),
            orderBy('timestamp', 'desc'), 
            limit(100)
          );
      }

      const querySnapshot = await getDocs(q);
      const recordsData = [];
      querySnapshot.forEach((doc) => {
        recordsData.push({ id: doc.id, ...doc.data() });
      });

      setRecords(recordsData);
      calculateStats(recordsData);
    } catch (error) {
      console.error('Error loading records:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (allRecords) => {
    const now = new Date();
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    
    const weekStart = new Date();
    weekStart.setDate(now.getDate() - 7);
    
    const monthStart = new Date();
    monthStart.setMonth(now.getMonth() - 1);

    let todayRev = 0, weekRev = 0, monthRev = 0, totalRev = 0;

    allRecords.forEach(record => {
      const amount = record.amount || 0;
      const timestamp = record.timestamp?.toDate ? record.timestamp.toDate() : new Date(record.timestamp);
      
      totalRev += amount;
      
      if (timestamp >= todayStart) {
        todayRev += amount;
      }
      if (timestamp >= weekStart) {
        weekRev += amount;
      }
      if (timestamp >= monthStart) {
        monthRev += amount;
      }
    });

    setStats({
      todayRevenue: todayRev,
      weekRevenue: weekRev,
      monthRevenue: monthRev,
      totalRevenue: totalRev
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const filteredRecords = records.filter(record => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      record.name?.toLowerCase().includes(search) ||
      record.licensePlate?.toLowerCase().includes(search) ||
      record.email?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Revenue Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-600 uppercase">Today's Revenue</div>
          <div className="mt-2 text-3xl font-bold text-green-600">
            ${stats.todayRevenue.toFixed(2)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-600 uppercase">This Week</div>
          <div className="mt-2 text-3xl font-bold text-green-600">
            ${stats.weekRevenue.toFixed(2)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-600 uppercase">This Month</div>
          <div className="mt-2 text-3xl font-bold text-green-600">
            ${stats.monthRevenue.toFixed(2)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-600 uppercase">All Time</div>
          <div className="mt-2 text-3xl font-bold text-green-600">
            ${stats.totalRevenue.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 gap-4">
          {/* Search */}
          <div className="flex-1 max-w-lg">
            <input
              type="text"
              placeholder="Search by name, license plate, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Time Filter */}
          <div className="flex space-x-2">
            {['all', 'today', 'week', 'month'].map((filterOption) => (
              <button
                key={filterOption}
                onClick={() => setFilter(filterOption)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  filter === filterOption
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {filterOption.charAt(0).toUpperCase() + filterOption.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Records Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date/Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  License Plate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRecords.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    No payment records found
                  </td>
                </tr>
              ) : (
                filteredRecords.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(record.timestamp)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{record.name}</div>
                      {record.email && (
                        <div className="text-sm text-gray-500">{record.email}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.licensePlate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.duration || record.hours ? `${record.hours || 0}h` : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                      ${(record.amount || 0).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        record.paymentStatus === 'completed' || record.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {record.paymentStatus || record.status || 'completed'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Export Button */}
      <div className="flex justify-end">
        <button
          onClick={() => {
            const csv = [
              ['Date', 'Customer', 'Email', 'License Plate', 'Duration', 'Amount', 'Status'],
              ...filteredRecords.map(r => [
                formatDate(r.timestamp),
                r.name,
                r.email || '',
                r.licensePlate,
                r.duration || r.hours || '',
                (r.amount || 0).toFixed(2),
                r.paymentStatus || r.status || 'completed'
              ])
            ].map(row => row.join(',')).join('\n');

            const blob = new Blob([csv], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `payment-records-${new Date().toISOString().split('T')[0]}.csv`;
            a.click();
          }}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Export to CSV
        </button>
      </div>
    </div>
  );
};

export default OwnerPaymentRecords;